// src/hooks/useCurrentAgent.ts
//
// Resolves the logged-in staff user's itsm_agents row (itsm_agents.user_id
// -> auth.uid()). This is what replaces the hardcoded performed_by:'Agent'
// / author_name:'Support Agent' strings that used to appear throughout the
// ITSM ticket pages: any code that needs "who is acting right now" should
// use this hook's `agent` instead of a literal string.
//
// If a staff user has ITSM section access (canViewSection('ITSM')) but no
// matching itsm_agents row, `agent` stays null and `notProvisioned` is
// true — callers should show a "contact an ITSM admin" state rather than
// silently falling back to a placeholder name.

import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../supabaseClient'

export interface CurrentAgent {
    agentId: number
    userId: string
    name: string
    email: string
    role: 'agent' | 'admin' | string
    isItsmAdmin: boolean
    isActive: boolean
}

interface UseCurrentAgentResult {
    agent: CurrentAgent | null
    loading: boolean
    notProvisioned: boolean
    refresh: () => void
}

export function useCurrentAgent(): UseCurrentAgentResult {
    const [agent, setAgent] = useState<CurrentAgent | null>(null)
    const [loading, setLoading] = useState(true)
    const [notProvisioned, setNotProvisioned] = useState(false)
    const [tick, setTick] = useState(0)

    const refresh = useCallback(() => setTick(t => t + 1), [])

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            setLoading(true)
            setNotProvisioned(false)

            const { data: { session } } = await supabase.auth.getSession()
            if (!session?.user) {
                if (!cancelled) { setAgent(null); setLoading(false) }
                return
            }

            const { data: row } = await supabase
                .from('itsm_agents')
                .select('id, user_id, name, email, role, is_active')
                .eq('user_id', session.user.id)
                .maybeSingle()

            if (cancelled) return

            if (!row || row.is_active === false) {
                setAgent(null)
                setNotProvisioned(true)
                setLoading(false)
                return
            }

            setAgent({
                agentId: row.id,
                userId: row.user_id,
                name: row.name,
                email: row.email,
                role: row.role || 'agent',
                isItsmAdmin: row.role === 'admin',
                isActive: row.is_active !== false,
            })
            setLoading(false)
        }

        load()
        return () => { cancelled = true }
    }, [tick])

    return { agent, loading, notProvisioned, refresh }
}
